/*
 * auto-captions-command-palette.js — Phase-1 chat editor for /auto-captions.
 *
 * Captions.ai / ChatCut charge for natural-language editing. Phase 1 covers
 * ~90 % of the practical command space with a small regex grammar that maps
 * one-line user input to existing window.__acXxx mutators. Future phases
 * (LLM intent parsing, multi-turn dialogue) layer on top of the same
 * { action, params } intermediate representation.
 *
 * Public surface (assigned to window.__acCommandPalette):
 *   mount(containerEl)   — inserts a <details> docked at top of editor card.
 *   parse(input)         — { action, params } | { action:'unknown', ... }.
 *   execute(parsed)      — dispatches to window.__acApplyPreset, etc.
 *
 * Tested under tests/js/auto-captions-command-palette.test.mjs.
 */
(function(){
    'use strict';

    if (typeof window === 'undefined') return;

    /* ─────────────────────────────────────────────────────────────
       parse() — regex grammar.

       Order matters: more specific patterns first. Whitespace at the
       edges is trimmed; matching is case-insensitive. Returned shape
       is always { action, params } so execute() can switch cleanly.

       Supported:
         preset hormozi                → { action:'preset',      params:{name:'hormozi'} }
         color #ff0000                 → { action:'color',       params:{value:'#ff0000'} }
         silence trim 0.5              → { action:'silenceTrim', params:{secs:0.5} }
         emphasis on / off             → { action:'emphasis',    params:{on:true|false} }
         translate es / ru / ko        → { action:'translate',   params:{target:'es'} }
         speakers on / off             → { action:'speakers',    params:{on:true|false} }
         find <text>                   → { action:'find',        params:{text:'...'} }
         replace <a> with <b>          → { action:'replace',     params:{from:'a', to:'b'} }
       ───────────────────────────────────────────────────────────── */
    function parse(input){
        var raw = String(input == null ? '' : input).trim();
        if (!raw) return { action: 'unknown', params: { raw: '' } };

        var lower = raw.toLowerCase();

        // 1) replace <a> with <b>     — must run before "find" so "replace foo with bar"
        //    isn't swallowed by the find branch.
        var mReplace = raw.match(/^replace\s+(.+?)\s+with\s+(.+)$/i);
        if (mReplace) {
            return {
                action: 'replace',
                params: { from: mReplace[1].trim(), to: mReplace[2].trim() }
            };
        }

        // 2) silence trim N            — N is seconds (float). Accept "silence trim 0.5"
        //    or the more natural "trim silence 0.5".
        var mSilence = lower.match(/^(?:silence\s+trim|trim\s+silence)\s+([0-9]*\.?[0-9]+)$/);
        if (mSilence) {
            return { action: 'silenceTrim', params: { secs: parseFloat(mSilence[1]) } };
        }

        // 3) preset <name>
        var mPreset = lower.match(/^preset\s+([a-z0-9\-_]+)$/);
        if (mPreset) {
            return { action: 'preset', params: { name: mPreset[1] } };
        }

        // 4) color #rrggbb  | color red                  (named colours pass through unchanged;
        //    the UI's swatch row keys by either hex or token.)
        var mColor = raw.match(/^color\s+(#[0-9a-f]{3,8}|[a-z][a-z0-9\-]*)$/i);
        if (mColor) {
            return { action: 'color', params: { value: mColor[1].toLowerCase() } };
        }

        // 5) emphasis on/off
        var mEmphasis = lower.match(/^emphasis\s+(on|off)$/);
        if (mEmphasis) {
            return { action: 'emphasis', params: { on: mEmphasis[1] === 'on' } };
        }

        // 6) translate <iso2|iso3>     — bare two/three-letter code per task spec.
        var mTranslate = lower.match(/^translate\s+([a-z]{2,3}(?:-[a-z]{2,4})?)$/);
        if (mTranslate) {
            return { action: 'translate', params: { target: mTranslate[1] } };
        }

        // 7) speakers on/off
        var mSpeakers = lower.match(/^speakers\s+(on|off)$/);
        if (mSpeakers) {
            return { action: 'speakers', params: { on: mSpeakers[1] === 'on' } };
        }

        // 8) find <text>               — anything after the word "find" (case-insensitive,
        //    preserves the user's original capitalisation for the search).
        var mFind = raw.match(/^find\s+(.+)$/i);
        if (mFind) {
            return { action: 'find', params: { text: mFind[1].trim() } };
        }

        return { action: 'unknown', params: { raw: raw } };
    }

    /* ─────────────────────────────────────────────────────────────
       execute() — dispatch to existing __acXxx mutators.

       Each branch is wrapped in a try/catch so a failing mutator
       can't take the input field down. Returns true on success,
       false on no-op / unknown.
       ───────────────────────────────────────────────────────────── */
    function execute(parsed){
        if (!parsed || !parsed.action) return false;
        try {
            switch (parsed.action) {
                case 'preset': {
                    var pname = parsed.params && parsed.params.name;
                    if (!pname) return false;
                    if (typeof window.__acApplyPreset === 'function') {
                        window.__acApplyPreset(pname);
                    } else {
                        // Fallback: synthesise a click on the matching .ac-preset chip,
                        // which is the canonical mutator path before pipeline.js exposes
                        // a direct entry point. Keeps the palette usable today.
                        var btn = document.querySelector('.ac-preset[data-preset="' + cssEscape(pname) + '"]');
                        if (btn) { btn.click(); } else { return false; }
                    }
                    return true;
                }
                case 'color': {
                    var cval = parsed.params && parsed.params.value;
                    if (!cval) return false;
                    if (typeof window.__acApplyColor === 'function') {
                        window.__acApplyColor(cval);
                    } else {
                        var cbtn = document.querySelector('.ac-color-btn[data-color="' + cssEscape(cval) + '"]');
                        if (cbtn) { cbtn.click(); } else { return false; }
                    }
                    return true;
                }
                case 'silenceTrim': {
                    var secs = parsed.params && parsed.params.secs;
                    if (typeof secs !== 'number' || !isFinite(secs)) return false;
                    if (typeof window.__acSilenceTrim === 'function') {
                        window.__acSilenceTrim(secs);
                        return true;
                    }
                    // Fallback path: toggle the silence-trim checkbox and run cleanup.
                    var box = document.getElementById('ac-clean-silences');
                    if (box) { box.checked = true; }
                    if (typeof window.__acApplyCleanup === 'function') {
                        window.__acApplyCleanup({ silenceThresholdSec: secs });
                        return true;
                    }
                    return false;
                }
                case 'emphasis': {
                    var eon = !!(parsed.params && parsed.params.on);
                    if (typeof window.__acToggleEmphasis === 'function') {
                        window.__acToggleEmphasis(eon);
                        return true;
                    }
                    var et = document.getElementById('ac-emphasis-toggle');
                    if (et) { et.checked = eon; et.dispatchEvent(new Event('change', { bubbles: true })); return true; }
                    return false;
                }
                case 'translate': {
                    var tgt = parsed.params && parsed.params.target;
                    if (!tgt) return false;
                    var sel = document.getElementById('ac-translate-target');
                    if (sel) {
                        sel.value = tgt;
                        sel.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    if (typeof window.__acRunTranslate === 'function') {
                        window.__acRunTranslate();
                        return true;
                    }
                    return false;
                }
                case 'speakers': {
                    var son = !!(parsed.params && parsed.params.on);
                    var st = document.getElementById('ac-speakers-toggle');
                    if (st) { st.checked = son; st.dispatchEvent(new Event('change', { bubbles: true })); }
                    if (typeof window.__acApplySpeakers === 'function') {
                        window.__acApplySpeakers();
                    }
                    return true;
                }
                case 'find': {
                    var q = parsed.params && parsed.params.text;
                    if (!q) return false;
                    var fr = document.getElementById('ac-find-row');
                    var fi = document.getElementById('ac-find-input');
                    if (fr) fr.style.display = '';
                    if (fi) {
                        fi.value = q;
                        fi.dispatchEvent(new Event('input', { bubbles: true }));
                        try { fi.focus({ preventScroll: true }); } catch(_) { fi.focus(); }
                    }
                    return true;
                }
                case 'replace': {
                    var from = parsed.params && parsed.params.from;
                    var to   = parsed.params && parsed.params.to;
                    if (from == null || to == null) return false;
                    if (typeof window.__acReplaceInWords === 'function') {
                        var n = window.__acReplaceInWords(from, to, true);
                        if (typeof window.__acRebuildLines === 'function') window.__acRebuildLines();
                        return n > 0;
                    }
                    return false;
                }
                default:
                    return false;
            }
        } catch (err) {
            try { if (typeof window.__handleError === 'function') window.__handleError(err, 'command-palette'); } catch(_) {}
            return false;
        }
    }

    /* CSS.escape polyfill — the matcher selectors include user-supplied
       tokens, so a stray quote would break the querySelector. Keep it
       inline; the global polyfill is not guaranteed on legacy WebViews. */
    function cssEscape(s){
        if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
        return String(s).replace(/["\\\]\[]/g, '\\$&');
    }

    /* ─────────────────────────────────────────────────────────────
       mount() — UI.

       Renders a single-row <details> the user can collapse. The summary
       is the "Type a command" label; the body is an <input>. Submit on
       Enter (form so screen-readers announce it as one).
       ───────────────────────────────────────────────────────────── */
    function mount(containerEl){
        if (!containerEl || typeof containerEl.appendChild !== 'function') return null;
        if (containerEl.querySelector && containerEl.querySelector('.ac-cmd-palette')) {
            return containerEl.querySelector('.ac-cmd-palette');
        }

        var i18n = (window.__acI18n && typeof window.__acI18n === 'object') ? window.__acI18n : {};
        var titleText = i18n.command_palette_title || 'Type a command';
        var placeholder = i18n.command_palette_placeholder || 'Try: preset hormozi';
        var unknownMsg  = i18n.command_palette_unknown || 'Unknown command';

        var details = document.createElement('details');
        details.className = 'ac-cmd-palette lg-card';
        details.style.cssText = 'margin:0 0 10px;border-radius:10px;border:1px solid var(--border);background:var(--glass);backdrop-filter:blur(10px)';

        var summary = document.createElement('summary');
        summary.style.cssText = 'cursor:pointer;padding:8px 12px;font-size:13px;color:var(--text-primary);font-weight:600;display:flex;align-items:center;gap:6px';
        summary.textContent = titleText;
        details.appendChild(summary);

        var form = document.createElement('form');
        form.setAttribute('role', 'search');
        form.style.cssText = 'padding:8px 12px 12px;display:flex;gap:8px;align-items:center';

        var input = document.createElement('input');
        input.type = 'text';
        input.className = 'ac-cmd-palette-input lg-input';
        input.placeholder = placeholder;
        input.setAttribute('aria-label', titleText);
        input.autocomplete = 'off';
        input.spellcheck = false;
        input.style.cssText = 'flex:1;min-width:0;padding:6px 10px;font-size:13px;background:var(--bg-tertiary,#0b1020);color:var(--text-primary);border:1px solid var(--border);border-radius:8px';
        form.appendChild(input);

        form.addEventListener('submit', function(e){
            e.preventDefault();
            var parsed = parse(input.value);
            if (parsed.action === 'unknown') {
                if (typeof window.__toast === 'function') {
                    window.__toast(unknownMsg + ': "' + (parsed.params.raw || '') + '"', 'warning');
                }
                return;
            }
            var ok = execute(parsed);
            if (ok) {
                input.value = '';
                if (typeof window.__aeFeature === 'function') {
                    try { window.__aeFeature('command_palette_run', { action: parsed.action }); } catch(_) {}
                }
            } else if (typeof window.__toast === 'function') {
                window.__toast(unknownMsg, 'warning');
            }
        });

        details.appendChild(form);
        // Dock at the top of the container — `prepend` keeps the chat-style
        // dock above any pre-existing editor controls.
        if (typeof containerEl.prepend === 'function') {
            containerEl.prepend(details);
        } else {
            containerEl.insertBefore(details, containerEl.firstChild);
        }

        return details;
    }

    window.__acCommandPalette = {
        mount: mount,
        parse: parse,
        execute: execute,
    };
})();
